import React, { useContext, useEffect, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
  Tooltip,
} from "@nextui-org/react";
import dots from "../../../Assets/scrapbook/dots-vertical.svg";
import { Gallery } from "react-grid-gallery";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { PopUpContext } from "Context/PopUpContext";
import UploadTaskMedia from "./UploadTaskMedia";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AddTask from "./AddTask";
import ViewTask from "./ViewTask";
import DeleteTaskModal from "./DeleteTaskModal";
import TaskMediaApi from "../../../services/taskMediaApi";
import useSettings from "hooks/useSettings";
import { SettingsContext } from "Context/SettingsContext";

export default function CompletedTasks() {
  const [targetId, setTargetId] = useState(null);
  const [itemKey, setItemKey] = useState(null);
  const [editTaskModal, setEditTaskModal] = useState(false);
  const [viewTaskModal, setViewTaskModal] = useState(false);
  const [frondData, setFrondData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [uploadMediaModal, setUploadMediaModal] = useState(false);
  const [uploadTaskId, setUploadTaskId] = useState(null);
  const [taskMedia, setTaskMedia] = useState({});
  const [mediaErrors, setMediaErrors] = useState({});

  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const { frondId } = useParams();
  const authToken = localStorage.getItem("token");

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    const timeUnits = [
      { name: "year", seconds: 31536000 },
      { name: "month", seconds: 2592000 },
      { name: "day", seconds: 86400 },
      { name: "hour", seconds: 3600 },
      { name: "minute", seconds: 60 },
      { name: "second", seconds: 1 },
    ];

    for (const unit of timeUnits) {
      const interval = Math.floor(diffInSeconds / unit.seconds);
      if (interval >= 1) {
        return `${interval} ${unit.name}${interval > 1 ? "s" : ""} ago`;
      }
    }

    return "just now";
  };

  const fetchTaskMedia = async (taskId) => {
    if (taskMedia[taskId] || mediaErrors[taskId]) return;
    
    try {
      const media = await TaskMediaApi.getTaskMedia(authToken, taskId);
      
      if (Array.isArray(media) && media.length > 0) {
        setTaskMedia(prev => ({...prev, [taskId]: media}));
      } else {
        setTaskMedia(prev => ({...prev, [taskId]: []}));
      }
    } catch (error) {
      console.error('Error fetching media for task', taskId, error);
      setMediaErrors(prev => ({...prev, [taskId]: true }));
    }
  };

  const loadMedia = async (tasks) => {
    if (!tasks || tasks.length === 0) return;
    
    for (const item of tasks) {
      if (item?.task?.id) {
        await fetchTaskMedia(item.task.id).catch(err => console.warn(`Media load failed for task ${item.task.id}:`, err));
      }
    }
  };

  const getTaskGalleryImages = (taskId) => {
    const media = taskMedia[taskId];
    
    if (!media || !Array.isArray(media) || media.length === 0) {
      return [];
    }

    const images = media.filter(m => m.type === 'image' || m.mimeType?.startsWith('image/'));
    
    return images.map((img) => ({
      src: img.thumbnail || img.url || img.original_url,
      width: 1,
      height: 1,
      originalSrc: img.url || img.original_url,
      id: img.id,
      name: img.name || img.filename || `Image ${img.id}`,
      caption: img.caption || '',
    }));
  };

  // Get task documents
  const getTaskDocuments = (taskId) => {
    const media = taskMedia[taskId];
    if (!media || !Array.isArray(media)) return [];
    
    return media.filter(m => m.type === 'document');
  };

  // Get document icon
  // Handle upload media
  const handleUploadMedia = (taskId) => {
    setUploadTaskId(taskId);
    setUploadMediaModal(true);
  };

  const handleUploadComplete = () => {
    if (uploadTaskId) {
      fetchTaskMedia(uploadTaskId);
    }
    setUploadMediaModal(false);
    setUploadTaskId(null);
  };

  const getDocumentIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📄';
  };

  const openLightbox = (taskId, clickIndex) => {
    const media = taskMedia[taskId];
    
    if (!media || !Array.isArray(media) || media.length === 0) return;

    const images = media.filter(m => m.type === 'image' || m.mimeType?.startsWith('image/'));
    
    // Prepare images for lightbox
    const lightboxImagesList = images.map(img => ({
      src: img.url || img.original_url,
      title: img.name || img.filename || `Image ${img.id}`,
      caption: img.caption || '',
    }));

    setLightboxImages(lightboxImagesList);
    setPhotoIndex(clickIndex);
    setLightboxOpen(true);
  };

  const renderTaskMedia = (taskId) => {
    const galleryImages = getTaskGalleryImages(taskId);
    const documents = getTaskDocuments(taskId);
    
    if (galleryImages.length === 0 && documents.length === 0) return null;

    return (
      <div className="mt-3 mb-2 space-y-3">
        {galleryImages.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
               <span>
              <img width="15" height="15" src="https://img.icons8.com/ios/20/578FCA/image--v1.png" alt="image--v1"/> 
              </span> 
              <span className="text-sm font-medium text-cardGrayColor">Images ({galleryImages.length})</span>
            </div>
            <Gallery
              onClick={(index) => openLightbox(taskId, index)}
              enableImageSelection={false}
              images={galleryImages}
              containerWidth={400}
              thumbnailStyle={{
                cursor: "pointer",
                width: "80px",
                height: "80px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
              rowHeight={80}
              margin={4}
            />
          </div>
        )}
        
       {/* Documents List */}
{documents.length > 0 && (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-2">
       <span className="text-lg"><img width="15" height="15" src="https://img.icons8.com/ultraviolet/20/EB4C4C/live-folder.png" alt="live-folder"/></span>
      <span className="text-sm font-medium text-cardGrayColor">Documents ({documents.length})</span>
    </div>
    <div className="flex overflow-x-auto gap-2 pb-2">
      {documents.map((doc) => (
        <div 
          key={doc.id}
          className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer group"
          onClick={() => window.open(doc.url, '_blank')}
        >
          <span className="text-lg">
            {getDocumentIcon(doc.mimeType)}
          </span>
          <span className="text-sm font-medium text-textColor max-w-[120px] truncate">
            {doc.name || doc.filename || 'Document'}
          </span>
          <span className="text-xs text-cardGrayColor">
            {doc.sizeFormatted || `${(doc.size / 1024).toFixed(0)} KB`}
          </span>
        </div>
      ))}
    </div>
  </div>
)}
      </div>
    );
  };

  const getTasks = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `/projects/${frondId}/tasks`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const tasks = data?.response?.data?.filter(
        (item) => item?.task?.status === "completed"
      );

      setFrondData(tasks);
      
      if (tasks.length > 0) {
        loadMedia(tasks);
      }
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to fetch tasks", {
        autoClose: 1500,
        position: "bottom-right",
        hideProgressBar: true,
        className: "rounded-4xl custom-toast error-toast",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getTasks();
  }, [frondId]);

  const { isAddTask } = useContext(PopUpContext);

  useEffect(() => {
    if (isAddTask) {
      getTasks();
    }
  }, [isAddTask]);

  useEffect(() => {
    if (itemKey === "delete") {
      setOpenDeleteModal(true);
      setItemKey(null);
    } else if (itemKey === "edit") {
      setEditTaskModal(true);
      setItemKey(null);
    } else if (itemKey === "view") {
      setViewTaskModal(true);
      setItemKey(null);
    } else if (itemKey === "archive") {
      archiveTask();
      setItemKey(null);
    }
  }, [itemKey]);

  const openModal = (key, id) => {
    setItemKey(key);
    setTargetId(id);
  };

  async function archiveTask() {
    if (!targetId || isArchiving) return;

    setIsArchiving(true);
    
    try {
      const currentTask = frondData.find(item => item?.task?.id === targetId);
      
      if (!currentTask) {
        toast("Task not found", {
          autoClose: 1500,
          position: "bottom-right",
          hideProgressBar: true,
          className: "rounded-4xl custom-toast error-toast",
        });
        return;
      }

      const payload = {
        title: currentTask?.task?.title,
        description: currentTask?.task?.description || "",
        due_date: currentTask?.task?.due_date || null,
        assigned_to: currentTask?.assigned_to?.map(user => user.id) || [],
        status: "archived"
      };

      await axios.put(
        `/tasks/${targetId}`,
        payload,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      toast.success("Task archived successfully", {
        autoClose: 1500,
        position: "bottom-right",
        hideProgressBar: true,
        className: "rounded-4xl custom-toast success-toast",
      });
      
      await getTasks();
      
    } catch (error) {
      console.error("Archive error:", error.response?.data);
      toast.error(error?.response?.data?.message || "Failed to archive task", {
        autoClose: 1500,
        position: "bottom-right",
        hideProgressBar: true,
        className: "rounded-4xl custom-toast error-toast",
      });
    } finally {
      setIsArchiving(false);
      setTargetId(null);
    }
  }

  const handleDotsClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  useSettings({ projectId: frondId });
  const { frondSettings } = useContext(SettingsContext);

  const [showTask, setShowTask] = useState(false);

  useEffect(() => {
    if (frondSettings.can_edit_tasks && !frondSettings.is_owner) {
      setShowTask(true);
    } else if (frondSettings.is_owner) {
      setShowTask(true);
    } else {
      setShowTask(false);
    }
  }, [frondSettings]);

  // Pagination
  const ITEMS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (currentPage > Math.ceil(frondData.length / ITEMS_PER_PAGE)) {
      setCurrentPage(Math.max(Math.ceil(frondData.length / ITEMS_PER_PAGE), 1));
    }
  }, [frondData, currentPage]);

  const totalPages = Math.ceil(frondData.length / ITEMS_PER_PAGE);
  const currentItems = frondData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // User
  const [userId, setUserId] = useState(null);

  const getUserData = async () => {
    try {
      const res = await axios.get("/user", {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      setUserId(res.data?.id);
    } catch (error) {}
  };

  useEffect(() => {
    getUserData();
  }, []);

  return (
    <>
      <section className="completed-tasks">
        {frondData.length > 0 && (
          <div className="flex flex-col gap-[15px]">
            {currentItems.map((item, idx) => (
              <div
                key={item?.task?.id}
                className={`flex justify-between w-full gap-2.5 ${
                  idx !== currentItems.length - 1
                    ? "pb-[15px] border-b-1 border-solid border-b-grayBorder"
                    : ""
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <Link
                    className={`${
                      item?.owner?.id !== userId && "pointer-events-none"
                    }`}
                    to={item?.owner?.id === userId ? `/my-tasks` : null}
                  >
                    <Image
                      src={item?.owner?.img_url}
                      classNames={{
                        wrapper:
                          "phone:min-w-[45px] phone:min-h-[45px] max-w-[30px] max-h-[30px] rounded-full",
                      }}
                      className="phone:min-w-[45px] phone:min-h-[45px] max-w-[30px] max-h-[30px] rounded-full"
                      alt={item?.owner?.name}
                    />
                  </Link>
                  <div>
                    <h4 className="text-base text-textColor font-bold leading-[18.4px] mb-[3px] flex items-center min-[640px]:flex-nowrap flex-wrap gap-y-1">
                      <Link
                        className={`${
                          item?.owner?.id !== userId && "pointer-events-none"
                        }`}
                        to={item?.owner?.id === userId ? `/my-tasks` : null}
                      >
                        {item?.owner?.name || "-"}
                      </Link>
                      <span className="mx-1 text-cardGrayColor font-normal">
                        to
                      </span>
                      {item?.assigned_to?.length > 0 ? (
                        <div className="flex items-center gap-2">
                          {item?.assigned_to.map((subItem) => (
                            <Tooltip key={subItem?.id} content={subItem?.name}>
                              <Link
                                className={`${
                                  subItem?.id !== userId &&
                                  "pointer-events-none"
                                }`}
                                to={subItem?.id === userId ? `/my-tasks` : null}
                              >
                                <Image
                                  src={subItem?.img_url}
                                  classNames={{
                                    wrapper: `min-w-[30px] min-h-[30px] max-w-[30px] max-h-[30px] rounded-full ${
                                      subItem?.status
                                        ? "grayscale-0"
                                        : "grayscale"
                                    }`,
                                  }}
                                  className="rounded-full min-w-[30px] min-h-[30px] max-w-[30px] max-h-[30px]"
                                />
                              </Link>
                            </Tooltip>
                          ))}
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </h4>
                    <p className="text-sm text-cardGrayColor mb-2.5">
                      <span className="me-2.5">
                        {timeAgo(item?.task?.created_at) || "-"}
                      </span>
                      <span className="text-sm leading-[22px] text-greenTimeline">
                        Completed
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() => openModal("view", item?.task?.id)}
                      className="text-mainBlue leading-[22px] font-bold mb-[4px]"
                      disabled={isArchiving && targetId === item?.task?.id}
                    >
                      {item?.task?.title || "-"}
                    </button>
                    <p className="text-textColor leading-[22px] break-before-all">
                      {item?.task?.description || "-"}
                    </p>
                    {renderTaskMedia(item?.task?.id)}
                  </div>
                </div>
                {showTask ? (
                  <Dropdown
                    classNames={{
                      content:
                        "min-w-[88px] max-w-[88px] py-0 px-[5px] rounded-[10px]",
                    }}
                    placement="left-start"
                  >
                    <DropdownTrigger>
                      <button
                        className="w-[24px] h-[24px]"
                        onClick={handleDotsClick}
                        disabled={isArchiving}
                      >
                        <img
                          src={dots}
                          alt="Dots"
                          className="min-w-[24px] min-h-[24px]"
                        />
                      </button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Frond Actions"
                      onAction={(key) => openModal(key, item?.task?.id)}
                      disabledKeys={isArchiving ? ["archive", "edit", "delete", "upload"] : []}
                    >
                      <DropdownItem
                        key="edit"
                        className="text-textColor text-sm font-normal py-[2px] px-[5px] relative after:absolute after:left-0 after:right-0 after:bottom-[-2.5px] after:h-px after:bg-grayBorder mb-[5px] selected-action"
                      >
                        Edit
                      </DropdownItem>
                      {/* <DropdownItem
                        textValue="upload"
                        key="upload"
                        className="text-textColor text-sm font-normal py-[2px] px-[5px] relative after:absolute after:left-0 after:right-0 after:bottom-[-2.5px] after:h-px after:bg-grayBorder mb-[5px] selected-action"
                        onClick={(e) => {
                          e.preventDefault();
                          handleUploadMedia(item?.task?.id);
                        }}
                      >
                        Upload Media
                      </DropdownItem> */}
                      <DropdownItem
                        key="archive"
                        className="text-[#ce1983] text-sm font-normal py-[2px] px-[5px] relative after:absolute after:left-0 after:right-0 after:bottom-[-2.5px] after:h-px after:bg-grayBorder mb-[5px] selected-action"
                      >
                        Archive
                      </DropdownItem>
                      <DropdownItem
                        key="delete"
                        className="text-[#EB6270] text-sm font-normal py-[2px] px-[5px] selected-action"
                        color="danger"
                      >
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {frondData?.length > 9 && (
          <div className="flex justify-center items-center gap-2.5 mt-6">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="smallPhone:px-4 px-2.5 py-2 smallPhone:text-base text-xs bg-mainGradiant rounded-xl text-white font-medium disabled:opacity-50"
            >
              Prev
            </button>
            <span className="smallPhone:text-lg text-sm text-textColor font-semibold">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="smallPhone:px-4 px-2.5 py-2 smallPhone:text-base text-xs bg-mainGradiant rounded-xl text-white font-medium disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </section>

      {/* Lightbox for fullscreen image viewing */}
      {lightboxOpen && (
        <Lightbox
          mainSrc={lightboxImages[photoIndex]?.src}
          nextSrc={lightboxImages[(photoIndex + 1) % lightboxImages.length]?.src}
          prevSrc={lightboxImages[(photoIndex + lightboxImages.length - 1) % lightboxImages.length]?.src}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + lightboxImages.length - 1) % lightboxImages.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % lightboxImages.length)
          }
          imageTitle={lightboxImages[photoIndex]?.title}
          imageCaption={lightboxImages[photoIndex]?.caption}
          reactModalStyle={{ zIndex: 10001 }}
        />
      )}

      {editTaskModal && targetId && (
        <AddTask
          isOpen={editTaskModal}
          onClose={() => setEditTaskModal(false)}
          frondId={frondId}
          isModalOpen={true}
          taskId={targetId}
        />
      )}
      {viewTaskModal && targetId && (
        <ViewTask
          isOpen={viewTaskModal}
          onClose={() => setViewTaskModal(false)}
          frondId={frondId}
          taskId={targetId}
        />
      )}
      {openDeleteModal && targetId && (
        <DeleteTaskModal
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          taskId={targetId}
        />
      )}
      {uploadMediaModal && uploadTaskId && (
        <UploadTaskMedia
          isOpen={uploadMediaModal}
          onClose={() => {
            setUploadMediaModal(false);
            setUploadTaskId(null);
          }}
          taskId={uploadTaskId}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </>
  );
}
